import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppContext } from '../../contexts/auth';
import useApiCall from '../../hooks/useApiCall';
import useErrors from '../../hooks/useErrors';
import { useQuery } from '../../hooks/useQuery';
import AuthService from '../../services/authService';
import { isPasswordValid } from '../../utils/isPasswordValid';

export default function useResetPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const navigate = useNavigate();
  const { signOut } = useAppContext();
  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();
  const { apiCall } = useApiCall();
  const query = useQuery();
  const token = query.get('token');
  const hasToken = query.has('token');
  const email = query.get('email');

  useEffect(() => {
    if (!hasToken) {
      navigate('/');
    }
  }, [hasToken, navigate]);

  const isReadyToResetPassword = (password && errors.length === 0);

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value);

    if (!isPasswordValid(event.target.value)) {
      setError({ field: 'password', message: 'A senha deve conter pelo menos 8 caracteres, uma letra maiúscula, uma letra minúscula, um número e um caractere especial!' });
    } else {
      removeError('password');
    }
  }

  function handlePasswordConfirmationChange(event: ChangeEvent<HTMLInputElement>) {
    setPasswordConfirmation(event.target.value);

    if (event.target.value !== password) {
      setError({ field: 'passwordConfirmation', message: 'As senhas não coincidem!' });
    } else {
      removeError('passwordConfirmation');
    }
  }

  const resetPassword = useCallback(async () => {
    await apiCall({
      apiToCall: AuthService.changePassword,
      reqBody: JSON.stringify({
        newPassword: password,
        email: email ? decodeURIComponent(email as string) : '',
      }),
      specificToken: decodeURIComponent(token as string),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (apiResponse) => {
        if (!apiResponse.success) {
          toast.error(`Não foi possível alterar a senha (${apiResponse.message})`);
          return;
        }

        toast.success('Senha alterada com sucesso! Realize seu login com sua nova senha');
        signOut({ silent: true });
      },
      catchMessage: 'Erro. Por favor, tente novamente',
    });
  },
  [apiCall, email, password, signOut, token],
  );

  return {
    isLoading,
    getErrorMessageByFieldName,
    password,
    handlePasswordChange,
    passwordConfirmation,
    handlePasswordConfirmationChange,
    resetPassword,
    isReadyToResetPassword,
  };
}
